import { useCallback, useEffect } from 'react';
import { canvasSelectionStore } from '../stores/canvasSelectionStore';
import { useCanvasStores, useCanvasLayers } from '../stores/CanvasContext';
import { globalTasksStore } from '../../stores/globalTasksStore';
import { getNextTaskId } from '../../stores/taskIdStore';
import { isFocusInEditable } from '../../lib/domUtils';
import { buildClipboardFromSelection } from '../clipboard/buildClipboard';
import { preparePastePayload } from '../clipboard/pasteClipboard';
import type { PastePayload } from '../clipboard/pasteClipboard';
import { getClipboard, setClipboard } from '../clipboard/clipboardStore';
import { captureSelectionAsImage } from '../clipboard/captureSelectionAsImage';
import { screenToCanvas as screenToCanvasUtil } from '../types';
import type { CanvasStore } from '../stores/canvasStore';
import { useCanvasActions } from './useCanvasActions';

/** Build clipboard from current selection. Returns null if nothing selected. */
function buildCurrentClipboard(store: CanvasStore) {
  const sel = canvasSelectionStore.getSnapshot().context;
  if (sel.selectedEntityIds.length === 0) return null;

  const ctx = store.getSnapshot().context;
  const allEntities = [
    ...Object.values(ctx.nodes),
    ...Object.values(ctx.connectors),
    ...Object.values(ctx.groups),
  ];
  const globalTasksById = globalTasksStore.getSnapshot().context.tasksById;

  return buildClipboardFromSelection(
    sel.selectedEntityIds,
    allEntities,
    globalTasksById,
  );
}

/** Apply a prepared paste payload: create global tasks, batch-add entities, select them. */
function applyPastePayload(payload: PastePayload, store: CanvasStore) {
  for (const task of payload.newGlobalTasks) {
    globalTasksStore.send({ type: 'task.add', task });
  }
  store.send({ type: 'entity.batchAdd', entities: payload.entities });
  canvasSelectionStore.send({ type: 'entity.set', entityIds: payload.newEntityIds });
}

/** Clipboard action handlers only (no keyboard listener). Safe to call from multiple components. */
export function useCanvasClipboardActions() {
  const { store, panZoomStore, containerRef, txManager } = useCanvasStores();
  const layers = useCanvasLayers();

  const getCanvasCenter = useCallback(() => {
    const container = containerRef.current;
    if (!container) return { x: 100, y: 100 };
    const rect = container.getBoundingClientRect();
    const pz = panZoomStore.getSnapshot().context;
    return screenToCanvasUtil(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2,
      container, pz, 0,
    );
  }, [panZoomStore, containerRef]);

  const getActiveLayerId = useCallback(() => {
    return store.getSnapshot().context.activeLayerId ?? layers[0]?.id ?? '';
  }, [store, layers]);

  const getValidLayerIds = useCallback(() => {
    return new Set(layers.map(l => l.id));
  }, [layers]);

  const handleCopy = useCallback(() => {
    const clipboard = buildCurrentClipboard(store);
    if (clipboard) setClipboard(clipboard);
  }, [store]);

  const handleCut = useCallback(() => {
    const clipboard = buildCurrentClipboard(store);
    if (clipboard) setClipboard(clipboard);

    // Then delete (wrapped in transaction for single undo step)
    const sel = canvasSelectionStore.getSnapshot().context;
    txManager.begin('cut');
    for (const entityId of sel.selectedEntityIds) {
      store.send({ type: 'entity.remove', entityId });
    }
    txManager.commit();
    canvasSelectionStore.send({ type: 'entity.clear' });
  }, [store, txManager]);

  const handlePaste = useCallback(() => {
    const clipboard = getClipboard();
    if (!clipboard) return;

    const payload = preparePastePayload(
      clipboard,
      getCanvasCenter(),
      getActiveLayerId(),
      getValidLayerIds(),
      getNextTaskId,
    );

    applyPastePayload(payload, store);
  }, [store, getCanvasCenter, getActiveLayerId, getValidLayerIds]);

  const handleDuplicate = useCallback(() => {
    const clipboard = buildCurrentClipboard(store);
    if (!clipboard) return;

    // Duplicate with +40px offset
    const offsetCenter = {
      x: clipboard.originCenter.x + 40,
      y: clipboard.originCenter.y + 40,
    };

    const payload = preparePastePayload(
      clipboard,
      offsetCenter,
      getActiveLayerId(),
      getValidLayerIds(),
      getNextTaskId,
    );

    applyPastePayload(payload, store);
  }, [store, getActiveLayerId, getValidLayerIds]);

  const handleCopyAsImage = useCallback(async () => {
    const sel = canvasSelectionStore.getSnapshot().context;
    if (sel.selectedEntityIds.length === 0) return;

    const clipCtx = store.getSnapshot().context;
    const allEntities = [
      ...Object.values(clipCtx.nodes),
      ...Object.values(clipCtx.connectors),
      ...Object.values(clipCtx.groups),
    ];
    const selected = allEntities.filter(e => sel.selectedEntityIds.includes(e.id));
    if (selected.length === 0) return;

    const container = containerRef.current;
    if (!container) return;

    const pz = panZoomStore.getSnapshot().context;

    try {
      await captureSelectionAsImage({
        selectedEntities: selected,
        containerEl: container,
        panZoom: pz,
      });
    } catch {
      // User cancelled the sharing dialog or browser doesn't support capture
    }
  }, [store, panZoomStore, containerRef]);

  return { handleCopy, handleCopyAsImage, handleCut, handlePaste, handleDuplicate };
}

/** Max dimension for pasted images — larger images are downscaled. */
const MAX_IMAGE_DIMENSION = 1920;

/** Read a Blob as a data URL, optionally downscaling if larger than maxDim. */
function processImageBlob(blob: Blob, maxDim: number): Promise<{ dataUrl: string; naturalWidth: number; naturalHeight: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const rawUrl = reader.result as string;
      const img = new window.Image();
      img.onerror = reject;
      img.onload = () => {
        let { naturalWidth: w, naturalHeight: h } = img;

        // Downscale if necessary
        if (w > maxDim || h > maxDim) {
          const scale = maxDim / Math.max(w, h);
          const newW = Math.round(w * scale);
          const newH = Math.round(h * scale);
          const canvas = document.createElement('canvas');
          canvas.width = newW;
          canvas.height = newH;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0, newW, newH);
          resolve({ dataUrl: canvas.toDataURL('image/png'), naturalWidth: newW, naturalHeight: newH });
        } else {
          resolve({ dataUrl: rawUrl, naturalWidth: w, naturalHeight: h });
        }
      };
      img.src = rawUrl;
    };
    reader.readAsDataURL(blob);
  });
}

/** Clipboard actions + keyboard shortcuts. Call ONLY ONCE (in CanvasInteractionSetup). */
export function useCanvasClipboard() {
  const actions = useCanvasClipboardActions();
  const { handleCopy, handleCopyAsImage, handleCut, handlePaste, handleDuplicate } = actions;
  const { handleAddImage } = useCanvasActions();

  // Keyboard shortcuts — registered once
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (isFocusInEditable()) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;

      switch (e.key.toLowerCase()) {
        case 'c':
          if (e.shiftKey) {
            e.preventDefault();
            handleCopyAsImage();
          } else {
            e.preventDefault();
            handleCopy();
          }
          break;
        case 'x':
          e.preventDefault();
          handleCut();
          break;
        case 'v': {
          // Only preventDefault if we have internal clipboard content.
          // Otherwise let the browser fire the native 'paste' event for image paste.
          const clipboard = getClipboard();
          if (clipboard) {
            e.preventDefault();
            handlePaste();
          }
          break;
        }
        case 'd':
          e.preventDefault();
          handleDuplicate();
          break;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleCopy, handleCopyAsImage, handleCut, handlePaste, handleDuplicate]);

  // Native paste event listener for image pasting from browser clipboard
  useEffect(() => {
    function handleNativePaste(e: ClipboardEvent) {
      if (isFocusInEditable()) return;
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (!blob) return;
          processImageBlob(blob, MAX_IMAGE_DIMENSION).then(({ dataUrl, naturalWidth, naturalHeight }) => {
            handleAddImage(dataUrl, naturalWidth, naturalHeight);
          });
          return;
        }
      }
    }

    document.addEventListener('paste', handleNativePaste);
    return () => document.removeEventListener('paste', handleNativePaste);
  }, [handleAddImage]);

  return actions;
}
